import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Wallet, Download, Phone, Mail, MapPin, Building2,
  TrendingUp, TrendingDown, AlertTriangle, ShoppingCart, FileSpreadsheet,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { useCanEdit, useHasModule } from "@/context/AuthContext";
import { downloadFile } from "@/components/Pagination";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TABS = [["overview", "Overview"], ["ledger", "Ledger"],
              ["invoices", "Invoices"], ["bills", "Bills"], ["payments", "Payments"]];

const Stat = ({ label, value, tone = "", sub }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 font-heading text-xl font-bold tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>}
  </div>
);

export default function PartyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const hasPurchases = useHasModule("purchases");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [ledger, setLedger] = useState(null);
  const [range, setRange] = useState({ from_date: "", to_date: "" });

  const load = useCallback(() => {
    api.get(`/parties/${id}/profile`).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== "ledger") return;
    api.get(`/parties/${id}/ledger`, { params: {
      from_date: range.from_date || undefined, to_date: range.to_date || undefined } })
      .then(({ data }) => setLedger(data));
  }, [tab, id, range]);

  if (!data) return <div className="text-sm text-slate-500">Loading profile…</div>;

  const p = data.party;
  const s = data.stats;
  const roles = [p.is_customer && "Customer", p.is_supplier && "Supplier"].filter(Boolean);
  const owesUs = p.current_balance > 0;

  const exportLedger = async (format) => {
    try {
      await downloadFile(`/parties/${id}/ledger/export`,
        { format, from_date: range.from_date || undefined, to_date: range.to_date || undefined },
        `statement.${format}`);
    } catch {
      toast.error("Could not generate the statement");
    }
  };

  return (
    <div className="space-y-6" data-testid="party-profile-page">
      <div>
        <Link to={p.is_supplier && !p.is_customer ? "/suppliers" : "/customers"}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
          <ArrowLeft size={13} /> Back
        </Link>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
                {p.display_name}
              </h1>
              {roles.map((r) => (
                <span key={r} className="rounded bg-blue-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-blue-700">
                  {r}
                </span>
              ))}
            </div>
            {p.legal_name && p.legal_name !== p.display_name && (
              <div className="mt-0.5 text-sm text-slate-600">{p.legal_name}</div>
            )}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              {p.gstin && <span className="flex items-center gap-1"><Building2 size={12} /> <span className="font-mono">{p.gstin}</span></span>}
              {p.phone && <span className="flex items-center gap-1"><Phone size={12} /> {p.phone}</span>}
              {p.email && <span className="flex items-center gap-1"><Mail size={12} /> {p.email}</span>}
              {p.state_name && <span className="flex items-center gap-1"><MapPin size={12} /> {p.state_name}</span>}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {canEdit && p.is_customer && (
              <Button size="sm" onClick={() => navigate(`/invoices/new?party_id=${p.id}`)}
                data-testid="profile-new-invoice" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                <FileText size={15} /> New invoice
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => exportLedger("pdf")}
              data-testid="profile-statement-pdf" className="gap-1.5">
              <Download size={15} /> Statement
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={owesUs ? "They owe you" : "You owe them"}
          value={inr(Math.abs(p.current_balance))}
          tone={owesUs ? "text-rose-700" : "text-emerald-700"}
          sub="Net across sales and purchases" />
        <Stat label="Receivable" value={inr(s.receivable)}
          sub={`${s.invoice_count} invoice${s.invoice_count === 1 ? "" : "s"}`} />
        {hasPurchases && p.is_supplier
          ? <Stat label="Payable" value={inr(s.payable)} sub={`${s.bill_count} bill${s.bill_count === 1 ? "" : "s"}`} />
          : <Stat label="Total sales" value={inr(s.sales_total)} />}
        <Stat label="Overdue" value={inr(s.overdue_amount)}
          tone={s.overdue_amount > 0 ? "text-rose-700" : ""}
          sub={`${s.overdue_count} past due date`} />
      </div>

      {s.first_transaction && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><TrendingUp size={12} /> Received {inr(s.payments_in)}</span>
          <span className="flex items-center gap-1"><TrendingDown size={12} /> Paid {inr(s.payments_out)}</span>
          <span>First transaction {fmtDate(s.first_transaction)}</span>
          <span>Last {fmtDate(s.last_transaction)}</span>
          {p.credit_period_days > 0 && <span>Credit period {p.credit_period_days} days</span>}
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.filter(([k]) => (k !== "bills" || (hasPurchases && p.is_supplier)))
          .map(([k, label]) => (
          <button key={k} data-testid={`profile-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">Billing address</div>
            <div className="p-4 text-sm text-slate-600">
              {p.billing_address || <span className="text-slate-400">Not recorded</span>}
              {p.shipping_address && p.shipping_address !== p.billing_address && (
                <>
                  <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">Shipping</div>
                  <div>{p.shipping_address}</div>
                </>
              )}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">Recent activity</div>
            <div className="divide-y divide-slate-100">
              {[...data.documents.slice(0, 5).map((d) => ({
                  date: d.document_date, label: d.document_number || "Draft",
                  sub: d.document_type?.replace(/_/g, " "), amount: d.totals?.grand_total }))]
                .concat(data.payments.slice(0, 3).map((x) => ({
                  date: x.payment_date, label: x.payment_number,
                  sub: x.payment_type === "PAYMENT_IN" ? "Receipt" : "Payment", amount: x.amount })))
                .sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 8)
                .map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-4 py-2 text-xs">
                    <div className="min-w-0">
                      <div className="truncate font-mono">{r.label}</div>
                      <div className="text-[11px] text-slate-500">{r.sub} · {fmtDate(r.date)}</div>
                    </div>
                    <span className="shrink-0 font-mono tabular-nums">{inr(r.amount)}</span>
                  </div>
                ))}
              {!data.documents.length && !data.payments.length && (
                <div className="px-4 py-10 text-center text-sm text-slate-500">Nothing yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "ledger" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[11px]">From</Label>
              <Input type="date" className="h-9 w-40" data-testid="ledger-from" value={range.from_date}
                onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">To</Label>
              <Input type="date" className="h-9 w-40" data-testid="ledger-to" value={range.to_date}
                onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
            </div>
            <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="ledger-pdf"
              onClick={() => exportLedger("pdf")}><Download size={14} /> PDF</Button>
            <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="ledger-xlsx"
              onClick={() => exportLedger("xlsx")}><FileSpreadsheet size={14} /> Excel</Button>
            <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="ledger-csv"
              onClick={() => exportLedger("csv")}>CSV</Button>
          </div>

          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Date</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Particulars</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Reference</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Debit</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Credit</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ledger?.entries?.map((e, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-600">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2">{e.particulars}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{e.reference || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{e.debit ? inr(e.debit) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{e.credit ? inr(e.credit) : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums font-semibold">{inr(e.balance)}</td>
                  </tr>
                ))}
                {ledger && !ledger.entries.length && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                    No entries in this period
                  </td></tr>
                )}
              </tbody>
              {!!ledger?.entries?.length && (
                <tfoot>
                  <tr className="bg-slate-900 text-white">
                    <td colSpan={5} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                      Closing balance
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold">{inr(ledger.closing_balance)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <p className="text-[11px] text-slate-500">
            Debit increases what they owe you; credit reduces it. A purchase bill from this party
            appears as a credit, so a business you both buy from and sell to nets off here.
          </p>
        </div>
      )}

      {tab === "invoices" && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="profile-invoices">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Number</th>
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                <th className="px-4 py-2.5 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.documents.map((d) => (
                <tr key={d.id} onClick={() => navigate(`/invoices/${d.id}`)}
                  className="cursor-pointer hover:bg-blue-50/40 transition-colors duration-150">
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold">{d.document_number || "DRAFT"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(d.document_date)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{inr(d.totals?.grand_total)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-rose-700">
                    {d.balance_due > 0.01 ? inr(d.balance_due) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <StatusBadge status={d.status === "ISSUED" ? d.payment_status : d.status} />
                  </td>
                </tr>
              ))}
              {!data.documents.length && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">No invoices</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "bills" && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="profile-bills">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Bill no.</th>
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                <th className="px-4 py-2.5 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.bills.map((b) => (
                <tr key={b.id} onClick={() => navigate(`/purchases/bills/${b.id}`)}
                  className="cursor-pointer hover:bg-blue-50/40 transition-colors duration-150">
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold">{b.supplier_bill_number}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(b.bill_date)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{inr(b.totals?.grand_total)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-rose-700">
                    {b.balance_due > 0.01 ? inr(b.balance_due) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right text-[11px]">{b.payment_status?.replace("_", " ")}</td>
                </tr>
              ))}
              {!data.bills.length && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">No bills</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payments" && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="profile-payments">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Number</th>
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold">Mode</th>
                <th className="px-3 py-2.5 text-left font-semibold">Direction</th>
                <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.payments.map((x) => (
                <tr key={x.id}>
                  <td className="px-4 py-2.5 font-mono text-xs">{x.payment_number}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(x.payment_date)}</td>
                  <td className="px-3 py-2.5 text-xs">{x.payment_mode?.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className={x.payment_type === "PAYMENT_IN" ? "text-emerald-700" : "text-rose-700"}>
                      {x.payment_type === "PAYMENT_IN" ? "Received" : "Paid"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">{inr(x.amount)}</td>
                </tr>
              ))}
              {!data.payments.length && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">No payments</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
